import { baseApi } from 'api/baseApi';

type IdentityResult = { id: string; email?: string; name: string };
type IdentitiesResult = IdentityResult[];

export const historyApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Fetches identity metadata (id + name) for a historical actor referenced in e.g. `modifiedBy`.
     *
     * Purpose:
     *   When rendering history entries we often only have a raw identifier. This endpoint resolves
     *   a stable display name so the UI can show a human-friendly label instead of the bare ID.
     *
     * Polymorphic ID:
     *   The `id` parameter can represent different entity kinds that authored a change:
     *     - User ID (standard end user / admin user)
     *     - Service Account ID (automation / integration actor)
     *     - API Key ID (key-based action attribution)
     *   All of these are resolved through the same identity endpoint: `/users/identity/:id`.
     *
     * Error Handling:
     *   We throw early if `id` is undefined to avoid issuing a malformed request. The calling hook
     *   controls execution via RTK Query's `skip` option, so this throw should only
     *   occur if the hook is misused.
     *
     * Caching & Invalidation:
     *   Results are tagged with `{ type: 'UserIdentity', id }` so that any mutation impacting one
     *   of these identity sources can invalidate the specific cached entry without clearing all.
     */
    fetchHistoryIdentity: build.query<IdentityResult, { id: string }>({
      query: ({ id }) => ({
        url: `/users/identity/${id}`,
      }),
      // Tag strategy: single user history object keyed by requested user id.
      // Using a stable tag allows invalidation via mutations: invalidateTags: [{ type: 'UserIdentity', id }]
      providesTags: (result) =>
        result ? [{ type: 'UserIdentity', id: result.id }] : [],
    }),
    findHistoryIdentities: build.query<IdentitiesResult, { ids: string[] }>({
      query: (body) => ({
        url: `/users/identity/find`,
        method: 'POST',
        body,
      }),
      providesTags: (res) =>
        res?.map(({ id }) => ({ type: 'UserIdentity', id })) ?? [],
    }),
  }),
});
export const { useFetchHistoryIdentityQuery, useFindHistoryIdentitiesQuery } =
  historyApi;
